import React, { useState, useEffect, useRef } from "react";

export const MultipleDatesInShiftPopup = ({
  initialStartDate,
  initialEndDate,
  initialMode = "weekday",
  initialWeekdays = {},
  initialCustomDates = [],
  onConfirm,
  onClose
}) => {
  const [mode, setMode] = useState(initialMode);
  const [startDate, setStartDate] = useState(initialStartDate || "");
  const [endDate, setEndDate] = useState(initialEndDate || "");
  const [weekdays, setWeekdays] = useState({
    0: false, 1: false, 2: false, 3: false, 4: false, 5: false, 6: false,
    ...initialWeekdays
  });
  const [customDates, setCustomDates] = useState([...initialCustomDates]);
  const [generatedDates, setGeneratedDates] = useState([]);
  const customDateRef = useRef();

  const colors = {
    primary: "#2563EB",
    secondary: "#E5E7EB",
    danger: "#DC2626",
    bg: "#FFFFFF",
    text: "#111827",
    muted: "#6B7280",
    overlay: "rgba(0, 0, 0, 0.5)"
  };

  const styles = {
    overlay: {
      position: "fixed",
      top: 0, left: 0,
      width: "100vw", height: "100vh",
      backgroundColor: colors.overlay,
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      zIndex: 1000,
    },
    container: {
      backgroundColor: colors.bg,
      borderRadius: 12,
      padding: 24,
      width: 600,
      maxWidth: "95vw",
      boxShadow: "0 8px 16px rgba(0,0,0,0.2)",
      display: "flex",
      flexDirection: "column",
    },
    header: {
      marginBottom: 20,
      borderBottom: `1px solid ${colors.secondary}`,
      paddingBottom: 12,
    },
    title: { margin: 0, fontSize: 20, color: colors.text },
    modeToggle: { display: "flex", gap: 10, marginBottom: 20 },
    toggleButton: (active) => ({
      flex: 1,
      padding: "10px 0",
      backgroundColor: active ? colors.primary : colors.secondary,
      color: active ? colors.bg : colors.text,
      border: "none",
      borderRadius: 6,
      cursor: "pointer",
      fontWeight: 500,
    }),
    formGroup: { marginBottom: 20, display: "flex", flexDirection: "column" },
    label: { marginBottom: 6, fontSize: 14, color: colors.text },
    input: {
      padding: 10,
      border: `1px solid ${colors.secondary}`,
      borderRadius: 6,
      fontSize: 14,
    },
    checkboxContainer: {
      display: "flex",
      flexWrap: "wrap",
      gap: 12,
      marginTop: 8,
    },
    dateTag: {
      display: "flex",
      alignItems: "center",
      padding: "4px 6px", // mindre grå bakgrund
      borderRadius: 6,
      backgroundColor: colors.secondary,
      color: colors.text,
      fontSize: 13,
      userSelect: "none",
    },
    dateText: {
      flex: 1,
      cursor: "pointer",
    },
    removeButton: {
      marginLeft: 6,
      background: "transparent",
      border: "none",
      fontSize: 16, // större '×'
      cursor: "pointer",
      color: colors.danger, // röd knapp
      lineHeight: 1,
    },
    selectedDatesContainer: {
      display: "flex",
      flexWrap: "wrap",
      gap: 8,
      marginTop: 8,
    },
    emptyState: {
      fontStyle: "italic",
      color: colors.muted,
      marginTop: 8,
    },
    actions: { marginTop: 10, display: "flex", justifyContent: "flex-end", gap: 12 },
    button: (primary) => ({
      padding: "10px 18px",
      border: "none",
      borderRadius: 6,
      backgroundColor: primary ? colors.primary : colors.secondary,
      color: primary ? colors.bg : colors.text,
      cursor: "pointer",
      fontSize: 14,
    }),
  };

  const toggleWeekday = (day) =>
    setWeekdays(prev => ({ ...prev, [day]: !prev[day] }));

  const generateWeekdayDates = () => {
    const dates = [];
    if (!startDate || !endDate) { setGeneratedDates([]); return; }
    const start = new Date(startDate), end = new Date(endDate);
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      if (weekdays[d.getDay()]) {
        dates.push(d.toISOString().split("T")[0]);
      }
    }
    setGeneratedDates(dates);
  };

  useEffect(() => {
    if (mode === "weekday") generateWeekdayDates();
    else setGeneratedDates([...customDates].sort());
  }, [mode, startDate, endDate, weekdays, customDates]);

  const handleConfirm = () => {
    if (generatedDates.length > 0) {
      onConfirm({
        dates: generatedDates,
        startDate, endDate, mode, weekdays, customDates,
      });
    }
  };

  const removeCustomDate = (dateToRemove) => {
    setCustomDates(prev => prev.filter(d => d !== dateToRemove));
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.container} onClick={e => e.stopPropagation()}>
        <header style={styles.header}>
          <h3 style={styles.title}>Välj flera dagar för pass</h3>
        </header>

        {/* Mode Toggle */}
        <div style={styles.modeToggle}>
          <button
            style={styles.toggleButton(mode === "weekday")}
            onClick={(e) => {
              e.preventDefault()
              setMode("weekday")
            }}
          >
            Veckodagar
          </button>
          <button
            style={styles.toggleButton(mode === "custom")}
            onClick={(e) => {
              e.preventDefault()
              setMode("custom")
            }}
          >
            Välj datum
          </button>
        </div>

        {/* Weekday Mode */}
        {mode === "weekday" ? (
          <>
            <div style={styles.formGroup}>
              <label style={styles.label}>Startdatum</label>
              <input
                type="date"
                value={startDate}
                onChange={e => setStartDate(e.target.value)}
                style={styles.input}
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Slutdatum</label>
              <input
                type="date"
                value={endDate}
                onChange={e => setEndDate(e.target.value)}
                style={styles.input}
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Välj veckodagar</label>
              <div style={styles.checkboxContainer}>
                {[
                  [1, "Mån"], [2, "Tis"], [3, "Ons"],
                  [4, "Tor"], [5, "Fre"], [6, "Lör"], [0, "Sön"],
                ].map(([d, label]) => (
                  <div key={d} style={{ display: "flex", alignItems: "center" }}>
                    <input
                      type="checkbox"
                      checked={weekdays[d]}
                      onChange={() => toggleWeekday(d)}
                      style={{ marginRight: 6 }}
                    />
                    <span style={{ fontSize: 14, color: colors.text }}>{label}</span>
                  </div>
                ))}
              </div>
            </div>
          </>
        ) : (
          /* Custom Date Mode */
          <>
            <div style={{ ...styles.formGroup, flexDirection: "row", display: "flex", gap: 8 }}>
              <input
                type="date"
                ref={customDateRef}
                style={{ ...styles.input, flex: 1 }}
              />
              <button
                style={styles.button(true)}
                onClick={(e) => {
                  e.preventDefault()
                  const newDate = customDateRef.current.value;
                  if (newDate && !customDates.includes(newDate)) {
                    setCustomDates(prev => [...prev, newDate]);
                    customDateRef.current.value = "";
                  }
                }}
              >
                Lägg till
              </button>
            </div>
            <div style={styles.selectedDatesContainer}>
              {customDates.length > 0 ? (
                customDates.sort().map(date => (
                  <div key={date} style={styles.dateTag}>
                    <span
                      style={styles.dateText}
                      onClick={(e) => {
                        e.preventDefault()
                        setCustomDates(prev =>
                          prev.includes(date)
                            ? prev.filter(d => d !== date)
                            : [...prev, date]
                        )
                      }}
                    >
                      {date}
                    </span>
                    <button
                      style={styles.removeButton}
                      onClick={() => removeCustomDate(date)}
                    >
                      ×
                    </button>
                  </div>
                ))
              ) : (
                <div style={styles.emptyState}>Inga datum tillagda...</div>
              )}
            </div>
          </>
        )}

        
     {/* Valda datum */}
<section style={styles.formGroup}>
  <label style={styles.label}>Valda datum</label>
  {generatedDates.length > 0 ? (
    <div style={styles.selectedDatesContainer}>
      {generatedDates.map(d => (
        <span key={d} style={{
          padding: "6px 12px",
          borderRadius: 6,
          backgroundColor: "#45a4e3",  // bara färgen ändrad
          color: "#ffffff",              // vit text för kontrast
          fontSize: 13
        }}>
          {d}
        </span>
      ))}
    </div>
  ) : (
    <div style={styles.emptyState}>Inga datum valda...</div>
  )}
</section>


        {/* Actions */}
        <footer style={styles.actions}>
          <button style={styles.button(false)} onClick={onClose}>
            Avbryt
          </button>
          <button
            style={{
              ...styles.button(true),
              opacity: generatedDates.length ? 1 : 0.6,
              cursor: generatedDates.length ? "pointer" : "not-allowed"
            }}
            onClick={handleConfirm}
            disabled={!generatedDates.length}
          >
            Lägg till pass
          </button>
        </footer>
      </div>
    </div>
  );
};
